import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * Manages active message path (message tree) and sibling navigation.
 */
export function useMessageTree({ messages, isAgentChat }) {
    const [activeMessages, setActiveMessages] = useState([]);

    const updateActiveMessages = useCallback(messagesList => {
        const messagesWithActive = [...messagesList];
        const messageIdToMessage = new Map(messagesWithActive.map(msg => [msg.id, msg]));

        messagesWithActive.forEach(msg => msg.active = false);

        const childParentIds = new Set(messagesWithActive.filter(msg => msg.parentId).map(msg => msg.parentId));
        const leafMessages = messagesWithActive.filter(msg => !childParentIds.has(msg.id));

        const latestLeaf = leafMessages.reduce((latest, current) => {
            if (!latest || new Date(current.createdAt) > new Date(latest.createdAt)) {
                return current;
            }
            return latest;
        }, null);

        if (latestLeaf) {
            let currentMsg = latestLeaf;
            while (currentMsg) {
                currentMsg.active = true;
                currentMsg = currentMsg.parentId ? messageIdToMessage.get(currentMsg.parentId) : null;
            }
        }

        return messagesWithActive;
    }, []);

    const switchSibling = useCallback((message, direction) => {
        const siblings = messages.filter(msg =>
            msg.parentId === message.parentId &&
            msg.sender === message.sender
        );

        if (siblings.length === 0) return;

        const currentIndex = siblings.findIndex(msg => msg.id === message.id);
        const nextIndex = direction === 'next'
            ? (currentIndex + 1) % siblings.length
            : (currentIndex - 1 + siblings.length) % siblings.length;

        const selectedSibling = siblings[nextIndex];

        setActiveMessages(prevMessages => {
            const updatedMessages = [...prevMessages];
            const messageIdToMessage = new Map(updatedMessages.map(msg => [msg.id, msg]));

            updatedMessages.forEach(msg => msg.active = false);

            let currentMsg = selectedSibling;
            while (currentMsg) {
                currentMsg.active = true;
                currentMsg = currentMsg.parentId ? messageIdToMessage.get(currentMsg.parentId) : null;
            }

            const activateLatestChildPath = parentId => {
                const children = updatedMessages.filter(msg => msg.parentId === parentId);
                if (children.length === 0) return;

                const latestChild = children.reduce((latest, current) => {
                    if (!latest || new Date(current.createdAt) > new Date(latest.createdAt)) {
                        return current;
                    }
                    return latest;
                }, null);

                latestChild.active = true;
                activateLatestChildPath(latestChild.id);
            };

            activateLatestChildPath(selectedSibling.id);

            return updatedMessages;
        });
    }, [messages]);

    useEffect(() => {
        setActiveMessages(updateActiveMessages(messages));
    }, [messages, updateActiveMessages]);

    const lastMessageId = useMemo(() => {
        const assistantSender = isAgentChat ? 'agent' : 'assistant';
        const assistantMessages = activeMessages?.filter(
            msg => msg.sender === assistantSender && msg.active
        );
        return assistantMessages[assistantMessages?.length - 1]?.id || null;
    }, [activeMessages, isAgentChat]);

    return { activeMessages, switchSibling, lastMessageId };
}
